import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from '../assets/Siyaanishlogo1.png';

const ThankYou = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId") || "SYN-VANYA-2026";
  const [countersStarted, setCountersStarted] = useState(false);
  
  const impactRef = useRef(null);
  const starsCanvasRef = useRef(null);
  const constellationCanvasRef = useRef(null);

  // Star field animation
  useEffect(() => {
    const canvas = starsCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const stars = Array.from({ length: 160 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.2 + 0.2,
      op: Math.random() * 0.35 + 0.05,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.008 + 0.003,
    }));

    let t = 0;
    let animationId;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      t += 0.012;
      for (const s of stars) {
        const op = s.op * (0.5 + 0.5 * Math.sin(t * s.speed * 80 + s.phase));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${op})`;
        ctx.fill();
      }
      animationId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // Constellation lines animation
  useEffect(() => {
    const canvas = constellationCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const nodes = Array.from({ length: 30 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.12,
    }));

    let animationId;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 160) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / 160) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animationId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // Counter animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !countersStarted) {
            setCountersStarted(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (impactRef.current) {
      observer.observe(impactRef.current);
    }

    return () => observer.disconnect();
  }, [countersStarted]);

  // Confetti burst effect
  useEffect(() => {
    const createConfetti = () => {
      const colors = ["#C9A84C", "#E8C97A", "#8B6E2E", "#F5F0E8", "#7A9E7E"];
      const shapes = ["circle", "square", "diamond"];

      for (let i = 0; i < 55; i++) {
        const el = document.createElement("div");
        const color = colors[Math.floor(Math.random() * colors.length)];
        const shape = shapes[Math.floor(Math.random() * shapes.length)];
        const size = Math.random() * 5 + 2;
        const startX = 50 + (Math.random() - 0.5) * 10;
        const startY = 50 + (Math.random() - 0.5) * 5;

        el.style.cssText = `
          position: fixed;
          left: ${startX}vw;
          top: ${startY}vh;
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border-radius: ${shape === "circle" ? "50%" : shape === "diamond" ? "0" : "2px"};
          transform: ${shape === "diamond" ? "rotate(45deg)" : "rotate(0)"};
          opacity: 1;
          pointer-events: none;
          z-index: 100;
        `;
        document.body.appendChild(el);

        const angle = Math.random() * Math.PI * 2;
        const dist = 80 + Math.random() * 180;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist - 80;
        const dur = 1200 + Math.random() * 800;
        const start = performance.now();

        const animate = (now) => {
          const t = Math.min((now - start) / dur, 1);
          const ease = 1 - Math.pow(1 - t, 2);
          el.style.transform = `translate(${dx * ease}px, ${dy * ease + 60 * t * t}px) rotate(${t * 720}deg)`;
          el.style.opacity = String(1 - t);
          if (t < 1) {
            requestAnimationFrame(animate);
          } else {
            el.remove();
          }
        };
        requestAnimationFrame(animate);
      }
    };

    const timer1 = setTimeout(() => createConfetti(), 1400);
    const timer2 = setTimeout(() => createConfetti(), 2200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  // Parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      const orb1 = document.querySelector(".orb-1");
      const orb2 = document.querySelector(".orb-2");
      if (orb1) orb1.style.transform = `translate(${x * 0.6}px, ${y * 0.4}px)`;
      if (orb2) orb2.style.transform = `translate(${-x * 0.4}px, ${-y * 0.3}px)`;
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-hidden" style={{ background: "#0A0805" }}>
      <style jsx>{`
        @keyframes grainMove {
          0% { transform: translate(0,0) scale(1.05); }
          33% { transform: translate(-3%,2%) scale(1.05); }
          66% { transform: translate(2%,-3%) scale(1.05); }
          100% { transform: translate(-1%,1%) scale(1.05); }
        }
        @keyframes orbDrift1 {
          to { transform: translate(80px, 60px); }
        }
        @keyframes orbDrift2 {
          to { transform: translate(-60px, -80px); }
        }
        @keyframes orbDrift3 {
          to { transform: translate(-50%, calc(-50% + 40px)); }
        }
        @keyframes diamondPulse {
          0%,100% { opacity: 0.6; transform: rotate(45deg) scale(1); }
          50% { opacity: 1; transform: rotate(45deg) scale(1.3); }
        }
        @keyframes ringRotate {
          to { transform: rotate(360deg); }
        }
        @keyframes emblemGlow {
          0%,100% { filter: drop-shadow(0 0 20px rgba(201,168,76,0.25)); }
          50% { filter: drop-shadow(0 0 35px rgba(201,168,76,0.45)); }
        }
        @keyframes sealSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes riseIn {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroRise {
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Grain Texture */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          opacity: 0.028,
          animation: "grainMove 0.4s steps(3) infinite",
        }}
      />

      {/* Ambient Orbs */}
      <div
        className="orb-1 fixed rounded-full pointer-events-none z-0"
        style={{
          width: "600px",
          height: "600px",
          background: "radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%)",
          top: "-200px",
          left: "-200px",
          animation: "orbDrift1 18s ease-in-out infinite alternate",
        }}
      />
      <div
        className="orb-2 fixed rounded-full pointer-events-none z-0"
        style={{
          width: "500px",
          height: "500px",
          background: "radial-gradient(circle, rgba(122,158,126,0.05) 0%, transparent 70%)",
          bottom: "-150px",
          right: "-150px",
          animation: "orbDrift2 22s ease-in-out infinite alternate",
        }}
      />
      <div
        className="fixed rounded-full pointer-events-none z-0"
        style={{
          width: "350px",
          height: "350px",
          background: "radial-gradient(circle, rgba(201,168,76,0.04) 0%, transparent 70%)",
          top: "40%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          animation: "orbDrift3 15s ease-in-out infinite alternate",
        }}
      />

      {/* Canvas Backgrounds */}
      <canvas ref={starsCanvasRef} id="stars" className="fixed inset-0 pointer-events-none z-0" />
      <canvas ref={constellationCanvasRef} className="fixed inset-0 pointer-events-none z-0" />

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center py-16 px-6">
        <div className="w-full max-w-[680px]">

          {/* Top Ornament */}
          <div
            className="flex items-center justify-center gap-5 mb-12"
            style={{ animation: "riseIn 1.2s ease 0.3s forwards", opacity: 0 }}
          >
            <div className="w-20 h-px bg-gradient-to-r from-transparent to-[rgba(201,168,76,0.35)]" />
            <div
              className="w-1.5 h-1.5 bg-[#C9A84C]"
              style={{ transform: "rotate(45deg)", animation: "diamondPulse 3s ease-in-out infinite" }}
            />
            <div className="w-20 h-px bg-gradient-to-l from-transparent to-[rgba(201,168,76,0.35)]" />
          </div>

          {/* Logo */}
<div
  className="flex justify-center mb-9"
  style={{ animation: "riseIn 1.4s ease 0.6s forwards", opacity: 0 }}
>
  <img
    src={logo}
    alt="Siyaanish Logo"
     className="h-16 w-auto bg-transparent mix-blend-multiply transition-transform duration-300 group-hover:scale-105"
  />
</div>
          {/* Eyebrow */}
          <div
            className="text-center text-[#C9A84C] text-[11px] tracking-[0.55em] uppercase font-normal mb-5"
            style={{ animation: "riseIn 1s ease 1s forwards", opacity: 0 }}
          >
            Order Received · A New Custodian Has Arrived
          </div>

          {/* Headline */}
          <div className="text-center overflow-hidden mb-2">
            <div
              className="font-['Cinzel',serif] text-[clamp(28px,6vw,62px)] font-light tracking-[0.12em] leading-[1.1] text-[#F5F0E8]"
              style={{ animation: "heroRise 1.4s cubic-bezier(0.16,1,0.3,1) 1.2s forwards", opacity: 0, transform: "translateY(30px)" }}
            >
              You just changed<br />
              <span className="text-[#C9A84C] italic font-['Cormorant_Garamond',serif] font-light">the earth's story.</span>
            </div>
          </div>

          {/* Divider */}
          <div
            className="flex items-center justify-center gap-4 my-7 max-w-[340px] mx-auto"
            style={{ animation: "riseIn 1s ease 1.8s forwards", opacity: 0 }}
          >
            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(201,168,76,0.35)]" />
            <div className="text-[#C9A84C] text-sm opacity-70">◆</div>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(201,168,76,0.35)]" />
          </div>

          {/* Philosophy */}
          <div
            className="text-center text-[clamp(15px,2.2vw,20px)] italic font-light text-[rgba(245,240,232,0.65)] leading-[1.9] tracking-[0.025em] max-w-[540px] mx-auto mb-9"
            style={{ animation: "riseIn 1.2s ease 2.1s forwards", opacity: 0 }}
          >
            Most purchases end with a receipt.<br />
            Yours begins with a <em className="text-[#F5F0E8] not-italic font-normal">responsibility</em> —<br />
            and a piece of something <em className="text-[#F5F0E8] not-italic font-normal">ancient, rescued, and alive.</em>
          </div>

          {/* Impact Strip */}
          <div
            ref={impactRef}
            className="relative flex justify-center gap-[clamp(20px,4vw,60px)] my-9 py-8 px-10 max-w-[620px] mx-auto w-full border border-[rgba(201,168,76,0.12)] bg-[rgba(201,168,76,0.03)]"
            style={{ animation: "riseIn 1s ease 2.5s forwards", opacity: 0 }}
          >
            <div className="absolute -top-[5px] left-1/2 -translate-x-1/2 text-[rgba(201,168,76,0.3)] text-[8px]">◆</div>
            <div className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 text-[rgba(201,168,76,0.3)] text-[8px]">◆</div>

            <div className="flex flex-col items-center gap-1.5 flex-1">
              <div className="font-['Cinzel',serif] text-[clamp(20px,3.5vw,36px)] font-light text-[#C9A84C] tracking-[0.05em] leading-tight">
                {countersStarted ? "1" : "0"}
              </div>
              <div className="text-[clamp(8px,1.1vw,10px)] tracking-[0.4em] text-[rgba(201,168,76,0.5)] uppercase font-normal text-center leading-relaxed">
                Coconut shells<br />rescued from waste
              </div>
            </div>

            <div className="w-px bg-[rgba(201,168,76,0.15)] self-stretch" />

            <div className="flex flex-col items-center gap-1.5 flex-1">
              <div className="font-['Cinzel',serif] text-[clamp(20px,3.5vw,36px)] font-light text-[#C9A84C] tracking-[0.05em] leading-tight">
                {countersStarted ? "340g" : "0g"}
              </div>
              <div className="text-[clamp(8px,1.1vw,10px)] tracking-[0.4em] text-[rgba(201,168,76,0.5)] uppercase font-normal text-center leading-relaxed">
                CO₂ emissions<br />prevented
              </div>
            </div>

            <div className="w-px bg-[rgba(201,168,76,0.15)] self-stretch" />

            <div className="flex flex-col items-center gap-1.5 flex-1">
              <div className="font-['Cinzel',serif] text-[clamp(20px,3.5vw,36px)] font-light text-[#C9A84C] tracking-[0.05em] leading-tight">
                {countersStarted ? "25+" : "0"}
              </div>
              <div className="text-[clamp(8px,1.1vw,10px)] tracking-[0.4em] text-[rgba(201,168,76,0.5)] uppercase font-normal text-center leading-relaxed">
                Years this piece<br />will outlast plastic
              </div>
            </div>
          </div>

          {/* Kintsugi Promise */}
          <div
            className="max-w-[560px] mx-auto mb-10 py-7 px-9 bg-[rgba(201,168,76,0.04)] border-l-2 border-[rgba(201,168,76,0.4)] relative"
            style={{ animation: "riseIn 1.2s ease 2.9s forwards", opacity: 0 }}
          >
            <div className="absolute -top-2 left-7 text-6xl text-[rgba(201,168,76,0.2)] font-['Cormorant_Garamond',serif] leading-none">"</div>
            <p className="text-[clamp(13px,1.8vw,16px)] italic font-light text-[rgba(245,240,232,0.65)] leading-[1.85] tracking-[0.02em]">
              If your Siyaanish piece ever breaks, return it to us.
              We will mend it with <strong className="text-[#C9A84C] not-italic font-normal">gold</strong> —
              and send it back more precious than before.
              A piece that has lived twice carries twice the story.
              This is our promise to you. Not a policy. A <strong className="text-[#C9A84C] not-italic font-normal">philosophy.</strong>
            </p>
            <div className="mt-3.5 text-[10px] tracking-[0.4em] text-[rgba(201,168,76,0.35)] uppercase">
              The Kintsugi Promise · Siyaanish
            </div>
          </div>

          {/* Three Truths */}
          <div
            className="flex flex-col max-w-[580px] mx-auto mb-11 w-full"
            style={{ animation: "riseIn 1.2s ease 3.2s forwards", opacity: 0 }}
          >
            <div className="grid grid-cols-[44px_1fr] gap-5 py-5 border-b border-[rgba(201,168,76,0.08)]">
              <div className="font-['Cinzel',serif] text-[11px] text-[rgba(201,168,76,0.4)] tracking-[0.2em] pt-0.5">I</div>
              <div>
                <div className="text-[clamp(13px,1.8vw,16px)] font-normal text-[#F5F0E8] tracking-[0.04em] mb-1">You chose conscience over convenience.</div>
                <div className="text-[clamp(11px,1.4vw,13px)] font-light text-[rgba(245,240,232,0.45)] leading-relaxed tracking-[0.01em]">
                  That is the rarest form of luxury — the kind that costs the earth nothing and gives it everything.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-[44px_1fr] gap-5 py-5 border-b border-[rgba(201,168,76,0.08)]">
              <div className="font-['Cinzel',serif] text-[11px] text-[rgba(201,168,76,0.4)] tracking-[0.2em] pt-0.5">II</div>
              <div>
                <div className="text-[clamp(13px,1.8vw,16px)] font-normal text-[#F5F0E8] tracking-[0.04em] mb-1">What you own now is singular.</div>
                <div className="text-[clamp(11px,1.4vw,13px)] font-light text-[rgba(245,240,232,0.45)] leading-relaxed tracking-[0.01em]">
                  No factory made this. No mould shaped it. Nature did — and there is no other like it in the world.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-[44px_1fr] gap-5 py-5">
              <div className="font-['Cinzel',serif] text-[11px] text-[rgba(201,168,76,0.4)] tracking-[0.2em] pt-0.5">III</div>
              <div>
                <div className="text-[clamp(13px,1.8vw,16px)] font-normal text-[#F5F0E8] tracking-[0.04em] mb-1">You are part of what comes next.</div>
                <div className="text-[clamp(11px,1.4vw,13px)] font-light text-[rgba(245,240,232,0.45)] leading-relaxed tracking-[0.01em]">
                  Every Siyaanish custodian is proof that beautiful and responsible are not opposites. You are that proof now.
                </div>
              </div>
            </div>
          </div>

          {/* Order Reference */}
          <div
            className="text-center mb-9"
            style={{ animation: "riseIn 1s ease 3.5s forwards", opacity: 0 }}
          >
            <div className="text-[9px] tracking-[0.55em] text-[rgba(201,168,76,0.3)] uppercase mb-1.5">Order Sealed</div>
            <div className="font-['Cinzel',serif] text-[clamp(11px,1.5vw,13px)] tracking-[0.35em] text-[rgba(201,168,76,0.5)]">
              #{orderId}
            </div>
          </div>

          {/* CTA Buttons */}
          <div
            className="flex gap-4 justify-center flex-wrap mb-12"
            style={{ animation: "riseIn 1s ease 3.8s forwards", opacity: 0 }}
          >
            <button
              onClick={() => navigate("/")}
              className="font-['Cormorant_Garamond',serif] text-[clamp(10px,1.3vw,12px)] tracking-[0.45em] uppercase text-[#0A0805] bg-[#C9A84C] border-none py-4 px-9 cursor-pointer font-medium transition-all duration-300 hover:bg-[#E8C97A] hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(201,168,76,0.25)]"
            >
              Continue Exploring
            </button>
            <button
              onClick={() => navigate(`/track-order?id=${orderId}`)}
              className="font-['Cormorant_Garamond',serif] text-[clamp(10px,1.3vw,12px)] tracking-[0.45em] uppercase text-[rgba(201,168,76,0.7)] bg-transparent border border-[rgba(201,168,76,0.25)] py-4 px-9 cursor-pointer font-normal transition-all duration-300 hover:border-[rgba(201,168,76,0.6)] hover:text-[#C9A84C] hover:-translate-y-0.5"
            >
              Share Your Story
            </button>
          </div>

          {/* Bottom Ornament */}
          <div
            className="flex flex-col items-center gap-3.5"
            style={{ animation: "riseIn 1s ease 4.1s forwards", opacity: 0 }}
          >
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-px bg-[rgba(201,168,76,0.15)]" />
              <div className="w-0.5 h-0.5 rounded-full bg-[rgba(201,168,76,0.3)]" />
              <div className="w-12 h-px bg-[rgba(201,168,76,0.15)]" />
            </div>
            <div className="font-['Cinzel',serif] text-[clamp(10px,1.4vw,12px)] tracking-[0.6em] text-[rgba(201,168,76,0.4)] uppercase font-light">
              Siyaanish
            </div>
            <div className="text-[clamp(10px,1.3vw,12px)] italic font-light text-[rgba(245,240,232,0.2)] tracking-[0.08em]">
              Made from what the earth already gave us.
            </div>
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-px bg-[rgba(201,168,76,0.15)]" />
              <div className="w-0.5 h-0.5 rounded-full bg-[rgba(201,168,76,0.3)]" />
              <div className="w-12 h-px bg-[rgba(201,168,76,0.15)]" />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ThankYou;